<template>
    <Dialog :open="open" @update:open="handleOpenChange">
        <DialogScrollContent
            class="my-4 flex w-full max-w-[95vw] flex-col rounded-lg border-none bg-white p-0 shadow-lg sm:max-w-3xl lg:max-w-4xl"
        >
            <DialogHeader class="flex-shrink-0 border-b border-slate-100">
                <DialogTitle class="px-4 py-4 sm:px-6">
                    <div>
                        <h3 class="text-lg font-semibold text-slate-900">
                            {{ hasExistingSignature ? 'Edit Signature' : 'Add Signature' }}
                        </h3>
                        <p v-if="account?.email_address" class="mt-1 text-sm font-normal text-slate-500">
                            {{ account.email_address }}
                        </p>
                    </div>
                </DialogTitle>
            </DialogHeader>

            <div class="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
                <div>
                    <EmailRichTextEditor
                        ref="signatureEditor"
                        v-model="signature"
                        image-mode="embedded"
                        @error="saveError = $event"
                    />

                    <div v-if="saveError" class="mt-3 rounded-md bg-red-50 p-2 text-sm text-red-700 ring-1 ring-red-600/10">
                        {{ saveError }}
                    </div>
                </div>

                <div class="flex h-[380px] flex-col rounded-md border border-slate-200">
                    <div class="flex-shrink-0 border-b border-slate-100 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                        Preview
                    </div>
                    <div
                        v-if="hasSignaturePreview"
                        class="signature-preview min-h-0 flex-1 overflow-auto p-3 text-sm text-slate-700"
                        v-html="sanitizedSignature"
                    ></div>
                    <div v-else class="min-h-0 flex-1 p-3 text-sm italic text-slate-400">No signature content.</div>
                </div>
            </div>

            <DialogFooter class="flex-shrink-0 border-t border-slate-100 px-4 py-4 sm:px-6">
                <Button type="button" variant="outline" @click="$emit('close')">Cancel</Button>
                <Button type="button" :disabled="saving" @click="saveSignature">
                    <AppIcon v-if="saving" name="spinner-third" class="mr-2 animate-spin" />
                    Save
                </Button>
            </DialogFooter>
        </DialogScrollContent>
    </Dialog>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { Dialog, DialogHeader, DialogTitle, DialogFooter, DialogScrollContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import EmailRichTextEditor from '@/components/email/EmailRichTextEditor.vue'
import { axios } from '@/utils/axios'
import { isBlankSignatureHtml, sanitizeEmailSignature } from '@/utils/email/signatures'

export default {
    name: 'EditSignatureModal',

    components: {
        Dialog,
        DialogHeader,
        DialogTitle,
        DialogFooter,
        DialogScrollContent,
        Button,
        EmailRichTextEditor,
    },

    props: {
        open: Boolean,
        account: Object,
    },

    emits: ['close', 'updated'],

    setup(props, { emit }) {
        const signature = ref('')
        const saving = ref(false)
        const saveError = ref('')
        const signatureEditor = ref(null)

        const sanitizedSignature = computed(() => sanitizeEmailSignature(signature.value))
        const hasSignaturePreview = computed(() => !isBlankSignatureHtml(sanitizedSignature.value))
        const hasExistingSignature = computed(() => !isBlankSignatureHtml(props.account?.signature || ''))

        function setEditorContent(value = '') {
            const cleanSignature = sanitizeEmailSignature(value)
            signature.value = cleanSignature
            saveError.value = ''
            signatureEditor.value?.setContent(cleanSignature)
        }

        watch(
            () => props.account?.id,
            () => {
                if (props.open) {
                    setEditorContent(props.account?.signature || '')
                }
            },
            { immediate: true },
        )

        watch(
            () => props.open,
            (isOpen) => {
                if (isOpen) {
                    setEditorContent(props.account?.signature || '')
                }
            },
        )

        async function saveSignature() {
            if (!props.account?.id) {
                return
            }

            const cleanSignature = sanitizeEmailSignature(signatureEditor.value?.getHTML() || signature.value)
            const signatureToSave = isBlankSignatureHtml(cleanSignature) ? '' : cleanSignature

            saving.value = true
            saveError.value = ''

            try {
                const { data } = await axios.patch(`/email-accounts/${props.account.id}`, {
                    signature: signatureToSave,
                })
                const account = data?.payload?.account || {
                    ...props.account,
                    signature: signatureToSave,
                }

                emit('updated', account)
                emit('close')
            } catch (err) {
                console.error('Error saving signature:', err)
                saveError.value = 'Unable to save signature. Please try again.'
            } finally {
                saving.value = false
            }
        }

        function handleOpenChange(isOpen) {
            if (!isOpen) {
                emit('close')
            }
        }

        return {
            signature,
            sanitizedSignature,
            hasSignaturePreview,
            hasExistingSignature,
            saving,
            saveError,
            signatureEditor,
            saveSignature,
            handleOpenChange,
        }
    },
}
</script>

<style scoped>
.signature-preview :deep(a) {
    color: rgb(79 70 229);
    text-decoration: underline;
}

.signature-preview :deep(ul),
.signature-preview :deep(ol) {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
}

.signature-preview :deep(ul) {
    list-style-type: disc;
}

.signature-preview :deep(ol) {
    list-style-type: decimal;
}

.signature-preview :deep(img) {
    max-width: 100%;
    height: auto;
}

.signature-preview :deep(blockquote) {
    margin: 0.75rem 0;
    border-left: 3px solid rgb(203 213 225);
    padding-left: 0.75rem;
    color: rgb(71 85 105);
}
</style>
